import type { ApiClient } from './client';
import type {
  CommunityPost,
  CommunityPostInput,
  CommunityPostsPage,
} from '../../types/communityPosts';

export const communityPostsMethods = {
  async getClubPosts(clubId: number, page = 1): Promise<CommunityPostsPage> {
    const response = await this.makeRequest<CommunityPostsPage>(
      `/club_posts.php?club_id=${clubId}&page=${page}`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success) {
      throw new Error(response.error || response.message || 'Failed to load club updates');
    }
    return response.data || { items: [], page: 1, limit: 20, total: 0 };
  },

  async createClubPost(clubId: number, input: CommunityPostInput): Promise<CommunityPost> {
    const response = await this.makeRequest<CommunityPost>('/club_posts.php', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'create', club_id: clubId, ...input }),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to post update');
    }
    return response.data;
  },

  async updateClubPost(id: number, input: CommunityPostInput): Promise<CommunityPost> {
    const response = await this.makeRequest<CommunityPost>('/club_posts.php', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'update', id, ...input }),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to save update');
    }
    return response.data;
  },

  async deleteClubPost(id: number): Promise<void> {
    const response = await this.makeRequest('/club_posts.php', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'delete', id }),
    });
    if (!response.success) {
      throw new Error(response.error || response.message || 'Failed to delete update');
    }
  },

  async getEventPosts(eventId: number, page = 1): Promise<CommunityPostsPage> {
    const response = await this.makeRequest<CommunityPostsPage>(
      `/event_posts.php?event_id=${eventId}&page=${page}`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success) {
      throw new Error(response.error || response.message || 'Failed to load event updates');
    }
    return response.data || { items: [], page: 1, limit: 20, total: 0 };
  },

  async createEventPost(eventId: number, input: CommunityPostInput): Promise<CommunityPost> {
    const response = await this.makeRequest<CommunityPost>('/event_posts.php', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'create', event_id: eventId, ...input }),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to post update');
    }
    return response.data;
  },

  async updateEventPost(id: number, input: CommunityPostInput): Promise<CommunityPost> {
    const response = await this.makeRequest<CommunityPost>('/event_posts.php', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'update', id, ...input }),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to save update');
    }
    return response.data;
  },

  async deleteEventPost(id: number): Promise<void> {
    const response = await this.makeRequest('/event_posts.php', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'delete', id }),
    });
    if (!response.success) {
      throw new Error(response.error || response.message || 'Failed to delete update');
    }
  },
} satisfies ThisType<ApiClient>;
